import { useState, useEffect } from 'react'
import { Card, Form, Row, Col, Alert, Button, InputGroup, Spinner } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'

const getNumberInputValue = (value, fallback = '') => (
  value === undefined || value === null ? fallback : value
)

const parseNumberInputValue = (value) => {
  if (value === '') return ''
  const parsed = Number(value)
  return Number.isNaN(parsed) ? value : parsed
}

const TaskConfigPanel = ({
  selectedTasks,
  selectedAccounts = [],
  availableAccounts = {},
  taskConfigs,
  onConfigChange,
  taskManager,
  getConfigOverride,
  updateConfigOverride
}) => {
  const { updateGlobalTaskConfig, getGlobalTaskConfig } = useAppStore()
  const [tasks, setTasks] = useState([])
  const [tasksWithConfig, setTasksWithConfig] = useState([])
  const [loadingSelects, setLoadingSelects] = useState({})
  const [selectLoadErrors, setSelectLoadErrors] = useState({})
  const [checkboxGroupSearch, setCheckboxGroupSearch] = useState({})

  useEffect(() => {
    if (taskManager && selectedTasks.length > 0) {
      const taskObjects = selectedTasks.map(taskId => taskManager.getTask(taskId)).filter(Boolean)
      setTasks(taskObjects)
    } else {
      setTasks([])
    }
  }, [taskManager, selectedTasks])

  // 过滤出有配置项的任务，并初始化默认配置
  useEffect(() => {
    let isMounted = true

    const filterTasksWithConfig = async () => {
      const tasksWithConfig = []
      const configsToUpdate = {}
      const fallbackAccountKey = Object.keys(availableAccounts)[0]
      const configContext = {
        accountKey: selectedAccounts[0] || fallbackAccountKey,
      }
      
      for (const task of tasks) {
        try {
          const existingConfig = getConfigOverride ? getConfigOverride(task.id) : getGlobalTaskConfig(task.id)
          const uiConfig = await task.getUIConfig({
            ...configContext,
            currentConfig: existingConfig || {}
          })
          if (uiConfig && uiConfig.controls && uiConfig.controls.length > 0) {
            tasksWithConfig.push({
              task,
              uiConfig
            })
            
            // 初始化默认配置：如果全局配置中没有该任务的配置，则使用默认值
            if (!existingConfig || Object.keys(existingConfig).length === 0) {
              const defaultConfig = {}
              uiConfig.controls.forEach(control => {
                // 获取默认值
                let defaultValue = control.default
                if (defaultValue === undefined) {
                  if (control.type === 'select') {
                    const selectedOption = control.options.find(opt => opt.selected)
                    defaultValue = selectedOption ? selectedOption.value : control.options[0]?.value
                  } else if (control.type === 'checkbox' || control.type === 'switch') {
                    defaultValue = control.defaultValue !== undefined ? control.defaultValue : false
                  } else if (control.type === 'checkbox-group') {
                    defaultValue = []
                  } else if (control.type === 'number-row') {
                    const fields = control.fields || []
                    fields.forEach(field => {
                      if (field.default !== undefined) {
                        defaultConfig[field.key] = field.default
                      }
                    })
                  } else if (control.type === 'checkbox-number') {
                    defaultConfig[control.key] = control.defaultValue !== undefined ? control.defaultValue : false
                    if (control.numberDefault !== undefined) {
                      defaultConfig[control.numberKey] = control.numberDefault
                    }
                  }
                }
                
                if (defaultValue !== undefined) {
                  defaultConfig[control.key] = defaultValue
                }
              })
              
              // 收集需要更新的配置
              if (Object.keys(defaultConfig).length > 0) {
                if (updateConfigOverride) {
                  updateConfigOverride(task.id, defaultConfig)
                } else {
                  updateGlobalTaskConfig(task.id, defaultConfig)
                }
                configsToUpdate[task.id] = defaultConfig
              }
            }
          }
        } catch (error) {
          console.error(`获取任务 ${task.name} 配置失败:`, error)
        }
      }
      
      if (!isMounted) return

      setTasksWithConfig(tasksWithConfig)
      
      // 批量更新本地状态
      if (Object.keys(configsToUpdate).length > 0) {
        const newConfigs = {
          ...taskConfigs,
          ...configsToUpdate
        }
        onConfigChange(newConfigs)
      }
    }

    filterTasksWithConfig()

    return () => {
      isMounted = false
    }
  }, [tasks, selectedAccounts, availableAccounts, getGlobalTaskConfig, updateGlobalTaskConfig, taskConfigs, onConfigChange, getConfigOverride, updateConfigOverride])

  const handleConfigUpdate = (taskId, configKey, value, extraConfig = {}) => {
    
    // 更新全局任务配置
    const currentConfig = (getConfigOverride ? getConfigOverride(taskId) : getGlobalTaskConfig(taskId)) || {}
    const newConfig = {
      ...currentConfig,
      [configKey]: value,
      ...extraConfig
    }
    
    // 更新全局配置
    if (updateConfigOverride) {
      updateConfigOverride(taskId, newConfig)
    } else {
      updateGlobalTaskConfig(taskId, newConfig)
    }
    
    // 同时更新本地状态以保持UI同步
    const newConfigs = {
      ...taskConfigs,
      [taskId]: newConfig
    }
    onConfigChange(newConfigs)
  }

  const updateControlOptions = (taskId, controlId, options) => {
    setTasksWithConfig(prev => prev.map(item => {
      if (item.task.id !== taskId) return item

      return {
        ...item,
        uiConfig: {
          ...item.uiConfig,
          controls: item.uiConfig.controls.map(control => (
            control.id === controlId ? { ...control, options } : control
          ))
        }
      }
    }))
  }

  const ensureCurrentOption = (options, currentValue, currentText) => {
    const hintOption = {
      value: "__refresh_hint__",
      text: "如需更改，请点击右侧刷新加载完整列表",
      disabled: true
    }

    if (Array.isArray(currentValue)) {
      const missingOptions = currentValue
        .filter(value => !options.some(option => String(option.value) === String(value)))
        .map(value => ({ value, text: String(value), selected: true }))
      return [hintOption, ...missingOptions, ...options]
    }

    if (currentValue === undefined || currentValue === null || currentValue === '') {
      return [hintOption, ...options]
    }

    const hasCurrentOption = options.some(option => String(option.value) === String(currentValue))
    if (hasCurrentOption) return [hintOption, ...options]

    return [
      {
        value: currentValue,
        text: currentText || `已配置选项 (ID:${currentValue})`,
        selected: true
      },
      hintOption,
      ...options
    ]
  }

  const loadSelectOptions = async (task, control, taskConfig) => {
    if (!control.loadOptionsOnFocus || !control.loadOptionsMethod || typeof task[control.loadOptionsMethod] !== 'function') {
      return
    }

    const loadingKey = `${task.id}:${control.id}`
    if (loadingSelects[loadingKey]) return

    setLoadingSelects(prev => ({ ...prev, [loadingKey]: true }))
    setSelectLoadErrors(prev => {
      const next = { ...prev }
      delete next[loadingKey]
      return next
    })

    try {
      const fallbackAccountKey = Object.keys(availableAccounts)[0]
      const accountKey = selectedAccounts[0] || fallbackAccountKey
      const loadedOptions = await task[control.loadOptionsMethod](accountKey)
      const options = Array.isArray(loadedOptions) ? loadedOptions : []
      const currentValue = taskConfig[control.key]
      const currentText = control.persistTextKey ? taskConfig[control.persistTextKey] : ''
      updateControlOptions(task.id, control.id, ensureCurrentOption(options, currentValue, currentText))
    } catch (error) {
      console.error(`加载任务 ${task.name} 配置选项失败:`, error)
      setSelectLoadErrors(prev => ({
        ...prev,
        [loadingKey]: error.message || '加载失败'
      }))
    } finally {
      setLoadingSelects(prev => {
        const next = { ...prev }
        delete next[loadingKey]
        return next
      })
    }
  }

  const renderConfigControl = (task, control) => {
    // 优先使用全局配置，如果没有则使用默认值
    const taskConfig = (getConfigOverride ? getConfigOverride(task.id) : getGlobalTaskConfig(task.id)) || {}
    const currentValue = taskConfig[control.key] !== undefined ? taskConfig[control.key] : control.default

    // 检查控件是否应该显示
    if (control.visible && typeof control.visible === 'function') {
      if (!control.visible(taskConfig)) {
        return null;
      }
    }

    switch (control.type) {
      case 'interval':
        return (
          <Form.Group key={control.id} className="mb-3">
            <Form.Label>{control.label}</Form.Label>
            <div className="input-group">
              <Form.Control
                type="number"
                min={control.min ?? 1}
                max={control.max ?? 120}
                value={getNumberInputValue(currentValue, control.default ?? 60)}
                onChange={(e) => handleConfigUpdate(task.id, control.key, parseNumberInputValue(e.target.value))}
              />
              <span className="input-group-text">分钟</span>
            </div>
          </Form.Group>
        )

      case 'select': {
        const loadingKey = `${task.id}:${control.id}`
        const selectOptions = Array.isArray(control.options) ? control.options : []
        const canRefresh = control.loadOptionsOnFocus && control.loadOptionsMethod
        const isLoading = Boolean(loadingSelects[loadingKey])
        return (
          <Form.Group key={control.id} className="mb-3">
            <Form.Label>{control.label}</Form.Label>
            <InputGroup>
              <Form.Select
                value={currentValue !== undefined ? String(currentValue) : selectOptions.find(opt => opt.selected)?.value || ''}
                disabled={isLoading}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  if (selectedValue === '__refresh_hint__') return;
                  // 根据选项值的类型进行转换
                  let convertedValue = selectedValue;
                  if (selectedValue === 'true') convertedValue = true;
                  else if (selectedValue === 'false') convertedValue = false;
                  else if (!isNaN(selectedValue) && selectedValue !== '') convertedValue = Number(selectedValue);

                  const selectedOption = selectOptions.find(option => String(option.value) === selectedValue)
                  const extraConfig = {}
                  if (control.persistTextKey) {
                    extraConfig[control.persistTextKey] = selectedOption?.text || ''
                  }
                  
                  handleConfigUpdate(task.id, control.key, convertedValue, extraConfig);
                }}
              >
                {isLoading && (
                  <option value={currentValue !== undefined ? String(currentValue) : ''}>
                    正在加载选项...
                  </option>
                )}
              {selectOptions.map(option => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.text}
                </option>
              ))}
              </Form.Select>
              {canRefresh && (
                <Button
                  variant={selectLoadErrors[loadingKey] ? 'outline-warning' : 'outline-secondary'}
                  onClick={() => loadSelectOptions(task, control, taskConfig)}
                  disabled={isLoading}
                  title={selectLoadErrors[loadingKey] ? `加载失败：${selectLoadErrors[loadingKey]}` : '刷新选项'}
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : '刷新'}
                </Button>
              )}
            </InputGroup>
            {selectLoadErrors[loadingKey] && (
              <Form.Text className="text-warning">
                动态列表加载失败，可点击“刷新”重试。
              </Form.Text>
            )}
          </Form.Group>
        )
      }

      case 'number':
        return (
          <Form.Group key={control.id} className="mb-3">
            <Form.Label>{control.label}</Form.Label>
            <Form.Control
              type="number"
              min={control.min ?? 1}
              max={control.max ?? 999}
              value={getNumberInputValue(currentValue, control.default ?? 1)}
              onChange={(e) => handleConfigUpdate(task.id, control.key, parseNumberInputValue(e.target.value))}
            />
          </Form.Group>
        )

      case 'number-row':
        return (
          <Form.Group key={control.id} className="mb-3">
            {control.label && <Form.Label>{control.label}</Form.Label>}
            <Row className="g-2">
              {(control.fields || []).map(field => {
                const fieldValue = taskConfig[field.key] !== undefined ? taskConfig[field.key] : field.default
                return (
                  <Col key={field.key} xs={12} md={(control.fields || []).length > 1 ? 6 : 12}>
                    <Form.Label className="small text-muted">{field.label}</Form.Label>
                    <Form.Control
                      type="number"
                      min={field.min ?? 1}
                      max={field.max ?? 999}
                      value={getNumberInputValue(fieldValue, 0)}
                      onChange={(e) => handleConfigUpdate(task.id, field.key, parseNumberInputValue(e.target.value))}
                    />
                  </Col>
                )
              })}
            </Row>
          </Form.Group>
        )

      case 'checkbox-number': {
        const numberValue = taskConfig[control.numberKey] !== undefined ? taskConfig[control.numberKey] : control.numberDefault
        return (
          <Form.Group key={control.id} className="mb-3">
            <div className="d-flex align-items-end gap-2">
              <Form.Check
                type="checkbox"
                id={control.id}
                label={control.label}
                checked={currentValue !== undefined ? currentValue : (control.defaultValue || false)}
                onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.checked)}
                className="flex-grow-1 pb-2"
              />
              <div style={{ width: '120px' }}>
                <Form.Label className="small text-muted">{control.numberLabel || '数值'}</Form.Label>
                <Form.Control
                  type="number"
                  min={control.min ?? 1}
                  max={control.max ?? 999}
                  value={getNumberInputValue(numberValue, 0)}
                  onChange={(e) => handleConfigUpdate(task.id, control.numberKey, parseNumberInputValue(e.target.value))}
                />
              </div>
            </div>
          </Form.Group>
        )
      }

      case 'text':
      case 'password':
        return (
          <Form.Group key={control.id} className="mb-3">
            <Form.Label>{control.label}</Form.Label>
            <Form.Control
              type={control.type === 'password' ? 'password' : 'text'}
              value={currentValue || control.default || ''}
              onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.value)}
              placeholder={control.placeholder}
            />
          </Form.Group>
        )

      case 'textarea':
        return (
          <Form.Group key={control.id} className="mb-3">
            <Form.Label>{control.label}</Form.Label>
            <Form.Control
              as="textarea"
              rows={control.rows || 2}
              value={currentValue || control.default || ''}
              onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.value)}
              placeholder={control.placeholder}
            />
          </Form.Group>
        )

      case 'time':
        return (
          <Form.Group key={control.id} className="mb-3">
            <Form.Label>{control.label}</Form.Label>
            <Form.Control
              type="time"
              value={currentValue || control.default || ''}
              onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.value)}
              step={control.step || 60}
            />
          </Form.Group>
        )

      case 'switch':
      case 'checkbox':
        return (
          <Form.Group key={control.id} className="mb-3">
            <Form.Check
              type="checkbox"
              label={control.label}
              checked={currentValue !== undefined ? currentValue : (control.defaultValue !== undefined ? control.defaultValue : (control.default || false))}
              onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.checked)}
            />
          </Form.Group>
        )

      case 'checkbox-group': {
        const loadingKey = `${task.id}:${control.id}`
        const canRefresh = control.loadOptionsOnFocus && control.loadOptionsMethod
        const isLoading = Boolean(loadingSelects[loadingKey])
        const selectedValues = Array.isArray(currentValue) ? currentValue : (control.default || [])
        const searchValue = checkboxGroupSearch[loadingKey] || ''
        const options = Array.isArray(control.options) ? control.options : []
        const selectedSet = new Set(selectedValues.map(value => String(value)))
        const filteredOptions = options.filter(option => {
          if (option.disabled) return true
          if (selectedSet.has(String(option.value))) return true
          if (!control.searchable || !searchValue.trim()) return true
          const text = [
            option.text,
            option.label,
            option.value,
            option.searchText
          ].filter(Boolean).join(" ").toLowerCase()
          return text.includes(searchValue.trim().toLowerCase())
        })
        return (
          <Form.Group key={control.id} className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="mb-0">{control.label}</Form.Label>
              {canRefresh && (
                <Button
                  variant={selectLoadErrors[loadingKey] ? 'outline-warning' : 'outline-secondary'}
                  size="sm"
                  onClick={() => loadSelectOptions(task, control, taskConfig)}
                  disabled={isLoading}
                  title={selectLoadErrors[loadingKey] ? `加载失败：${selectLoadErrors[loadingKey]}` : '刷新选项'}
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : '刷新'}
                </Button>
              )}
            </div>
            {control.searchable && (
              <Form.Control
                type="search"
                size="sm"
                className="mt-2"
                value={searchValue}
                onChange={(e) => setCheckboxGroupSearch(prev => ({
                  ...prev,
                  [loadingKey]: e.target.value
                }))}
                placeholder="搜索武将名"
              />
            )}
            <div
              className="mt-2"
              style={control.maxHeight ? { maxHeight: `${control.maxHeight}px`, overflowY: 'auto' } : undefined}
            >
              {isLoading && <Form.Text className="text-muted">正在加载选项...</Form.Text>}
              {filteredOptions.map(option => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`${control.id}-${option.value}`}
                  label={option.text}
                  checked={selectedValues.includes(option.value)}
                  disabled={option.disabled}
                  onChange={(e) => {
                    if (option.disabled) return
                    let newValues
                    if (e.target.checked) {
                      // 添加选项
                      newValues = [...selectedValues, option.value]
                    } else {
                      // 移除选项
                      newValues = selectedValues.filter(v => v !== option.value)
                    }
                    handleConfigUpdate(task.id, control.key, newValues)
                  }}
                  className="mb-1"
                />
              ))}
              {!isLoading && filteredOptions.length === 0 && (
                <Form.Text className="text-muted">没有匹配的选项</Form.Text>
              )}
            </div>
            {selectLoadErrors[loadingKey] && (
              <Form.Text className="text-warning">
                动态列表加载失败，可点击“刷新”重试。
              </Form.Text>
            )}
          </Form.Group>
        )
      }

      case 'stage-strategies': {
        const defaultPlans = Array.isArray(control.default) ? control.default : []
        const configuredPlans = Array.isArray(currentValue) ? currentValue : []
        const fallbackByPoint = new Map(defaultPlans.map(plan => [Number(plan.point), plan]))
        const configuredByPoint = new Map(configuredPlans.map(plan => [Number(plan?.point), plan]))
        const plans = Array.from({ length: 9 }, (_, index) => {
          const point = index + 1
          return {
            ...(fallbackByPoint.get(point) || {}),
            ...(configuredByPoint.get(point) || {}),
            point
          }
        })
        const updatePlan = (point, key, value) => {
          const nextPlans = plans.map(plan => (
            plan.point === point ? { ...plan, [key]: value } : { ...plan }
          ))
          handleConfigUpdate(task.id, control.key, nextPlans)
        }

        return (
          <Form.Group key={control.id} className="mb-3">
            <Form.Label>{control.label}</Form.Label>
            {control.description && (
              <Form.Text className="d-block text-muted mb-2">{control.description}</Form.Text>
            )}
            <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col" style={{ minWidth: '70px' }}>关卡</th>
                    <th scope="col" style={{ minWidth: '150px' }}>阵法</th>
                    <th scope="col" style={{ minWidth: '110px' }}>方案</th>
                    <th scope="col" style={{ minWidth: '130px' }}>失败重试次数</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map(plan => (
                    <tr key={plan.point}>
                      <th scope="row">第{plan.point}关</th>
                      <td>
                        <Form.Select
                          size="sm"
                          aria-label={`第${plan.point}关阵法`}
                          value={String(plan.matrixMid ?? '')}
                          onChange={(e) => updatePlan(plan.point, 'matrixMid', Number(e.target.value))}
                        >
                          {(control.matrixOptions || []).map(option => (
                            <option key={option.value} value={option.value}>{option.text}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          aria-label={`第${plan.point}关阵法方案`}
                          value={String(plan.matrixCase ?? '')}
                          onChange={(e) => updatePlan(plan.point, 'matrixCase', Number(e.target.value))}
                        >
                          {(control.caseOptions || []).map(option => (
                            <option key={option.value} value={option.value}>{option.text}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={1}
                          max={999}
                          aria-label={`第${plan.point}关失败重试次数`}
                          value={getNumberInputValue(plan.failureRetryLimit, 5)}
                          onChange={(e) => updatePlan(plan.point, 'failureRetryLimit', parseNumberInputValue(e.target.value))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Form.Group>
        )
      }

      case 'custom':
        return (
          <div key={control.id} className="mb-3" dangerouslySetInnerHTML={{ __html: control.html }} />
        )

      default:
        return null
    }
  }

  const renderBlackMarketLayout = (task, uiConfig) => {
    const controls = new Map((uiConfig.controls || []).map(control => [control.id || control.key, control]))
    const renderControl = (id) => {
      const control = controls.get(id)
      if (!control) return null
      return renderConfigControl(task, control)
    }

    return (
      <Row className="black-market-config g-4">
        <Col xs={12} lg={6}>
          <div className="black-market-section">
            <div className="black-market-section-title">刷新与预算</div>
            {renderControl("BlackMarketRefreshBudget")}
            {renderControl("BlackMarketAllowGoldPurchase")}
          </div>

          <div className="black-market-section">
            <div className="black-market-section-title">资源与道具</div>
            <div className="black-market-rule-grid">
              {renderControl("BlackMarketSilverRule")}
              {renderControl("BlackMarketTfcard1Rule")}
              {renderControl("BlackMarketTfcard2Rule")}
              {renderControl("BlackMarketTfcard3Rule")}
              {renderControl("BlackMarketJunlingRule")}
            </div>
          </div>
        </Col>

        <Col xs={12} lg={6}>
          <div className="black-market-section black-market-hero-section">
            <div className="black-market-section-title">武将目标</div>
            {renderControl("BlackMarketGeneralRule")}
            {renderControl("BlackMarketGeneralTargetNames")}
          </div>
        </Col>
      </Row>
    )
  }

  const resetAllConfigs = () => {
    // 重置所有全局任务配置
    selectedTasks.forEach(taskId => {
      if (updateConfigOverride) {
        updateConfigOverride(taskId, {})
      } else {
        updateGlobalTaskConfig(taskId, {})
      }
    })
    
    // 重置本地状态
    onConfigChange({})
  }

  if (selectedTasks.length === 0) {
    return (
      <Alert variant="info">
        请先在&quot;任务选择&quot;标签页中选择要配置的任务
      </Alert>
    )
  }

  if (tasksWithConfig.length === 0) {
    return (
      <Alert variant="info">
        所选任务均无需配置参数
      </Alert>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">任务参数配置</h6>
        <Button 
          variant="outline-secondary" 
          size="sm"
          onClick={resetAllConfigs}
        >
          重置所有配置
        </Button>
      </div>

      <Row className="g-3">
        {tasksWithConfig.map(({ task, uiConfig }) => (
          <Col key={task.id} xs={12} md={["black-market", "full-width"].includes(uiConfig.layout) ? 12 : 6} lg={["black-market", "full-width"].includes(uiConfig.layout) ? 12 : 4}>
            <Card className="h-100 task-config-card">
              <Card.Header className="d-flex align-items-center">
                <span className="me-2 fs-5">{task.icon}</span>
                <span className="fw-bold">{task.name}</span>
              </Card.Header>
              <Card.Body>
                {uiConfig.layout === "black-market" ? (
                  renderBlackMarketLayout(task, uiConfig)
                ) : uiConfig.layout === "two-column" ? (
                  <Row className="g-3">
                    {uiConfig.controls.map(control => (
                      <Col key={control.id || control.key} xs={12} md={6}>
                        {renderConfigControl(task, control)}
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div>
                    {uiConfig.controls.map(control => renderConfigControl(task, control))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  )
}

export default TaskConfigPanel
